/**
 * File upload validation and sanitization utilities
 * Implements OWASP security best practices for file uploads
 */

// Maximum file size: 25MB
export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

// Whitelist of allowed MIME types
export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword', // .doc
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
];

// Whitelist of allowed file extensions
export const ALLOWED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.xls',
  '.xlsx',
];

// Dangerous file extensions to block
const DANGEROUS_EXTENSIONS = [
  '.exe', '.bat', '.cmd', '.sh', '.ps1', '.msi', '.app',
  '.jar', '.js', '.vbs', '.scr', '.com', '.pif',
  '.reg', '.dll', '.sys', '.drv'
];

/**
 * Validate a single file
 * @param {File} file - File object to validate
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export function validateFile(file) {
  if (!file) {
    return { valid: false, error: "No file selected." };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `File is too large (${sizeMB}MB). Maximum size is 25MB.`
    };
  }

  // Check for zero-byte files
  if (file.size === 0) {
    return {
      valid: false,
      error: "File is empty."
    };
  }

  // Get file extension
  const extension = ('.' + file.name.split('.').pop()).toLowerCase();

  // Block dangerous extensions
  if (DANGEROUS_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: "File type is not allowed."
    };
  }

  // Check allowed extensions
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: "File type is not supported."
    };
  }

  // Check MIME type (if available)
  if (file.type && !ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: "File type is not supported."
    };
  }

  return { valid: true, error: null };
}

/**
 * Validate multiple files
 * @param {FileList|File[]} files - Files to validate
 * @returns {Object} - { valid: boolean, errors: string[], validFiles: File[] }
 */
export function validateFiles(files) {
  const fileArray = Array.from(files);
  const errors = [];
  const validFiles = [];

  if (fileArray.length === 0) {
    return { valid: false, errors: ["No files selected."], validFiles: [] };
  }

  // Limit number of files per upload
  const MAX_FILES_PER_UPLOAD = 50;
  if (fileArray.length > MAX_FILES_PER_UPLOAD) {
    return {
      valid: false,
      errors: ["Too many files selected. Please reduce the number of files and try again."],
      validFiles: []
    };
  }

  fileArray.forEach(file => {
    const result = validateFile(file);
    if (result.valid) {
      validFiles.push(file);
    } else {
      errors.push(result.error);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    validFiles
  };
}

/**
 * Sanitize filename to prevent path traversal and special character attacks
 * @param {string} filename - Original filename
 * @returns {string} - Sanitized filename
 */
export function sanitizeFilename(filename) {
  if (!filename) return 'unnamed_file';

  // Remove path components (../,  ..\, etc.)
  let sanitized = filename.replace(/^.*[\\\/]/, '');

  // Remove or replace dangerous characters
  // Allow: letters, numbers, dots, hyphens, underscores, spaces
  sanitized = sanitized.replace(/[^a-zA-Z0-9._\s-]/g, '_');

  // Prevent multiple dots (potential double extension attack)
  sanitized = sanitized.replace(/\.{2,}/g, '.');

  // Remove leading/trailing dots and spaces
  sanitized = sanitized.replace(/^[.\s]+|[.\s]+$/g, '');

  // Limit filename length
  const MAX_FILENAME_LENGTH = 255;
  if (sanitized.length > MAX_FILENAME_LENGTH) {
    const extension = sanitized.split('.').pop();
    const nameWithoutExt = sanitized.slice(0, sanitized.lastIndexOf('.'));
    sanitized = nameWithoutExt.slice(0, MAX_FILENAME_LENGTH - extension.length - 1) + '.' + extension;
  }

  // Ensure filename is not empty after sanitization
  if (!sanitized || sanitized === '.') {
    return 'unnamed_file';
  }

  return sanitized;
}

/**
 * Create a sanitized File object from an existing File
 * @param {File} file - Original file
 * @returns {File} - New file with sanitized name
 */
export function sanitizeFile(file) {
  const sanitizedName = sanitizeFilename(file.name);
  return new File([file], sanitizedName, {
    type: file.type,
    lastModified: file.lastModified
  });
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size (e.g., "1.5 MB")
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
